import { useState } from 'react';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { DialogStyled, SubHeader } from '@/components';
import { onQueryError, onQuerySuccess, useProduct } from '@/core';
import { EditProductUtil, ProductPossibleModificationsUtil, useDuplicateProduct } from '@/features';
import { useProductForm } from '@/features/product/hooks/useProductForm';
import { FOODS_AND_PRODUCTS_ROUTE } from '@/routes';
import palette from '@/theme/palette';

export const PageProductDetails = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const {
    queries: { useProductQuery },
    mutations: { deleteMutation },
  } = useProduct();
  const { duplicateProduct, isPending: isDuplicating } = useDuplicateProduct();
  const [isDeleteProductDialogOpen, setIsDeleteProductDialogOpen] = useState(false);

  const { data: product, isLoading, isError } = useProductQuery(Number(id));

  const { methods, onSubmit, isPending } = useProductForm(product?.data);

  if (isLoading) return <CircularProgress sx={{ margin: 'auto', mt: '40dvh' }} />;
  if (isError) return <div>{t('PRODUCT.LOADING_FAILURE')}</div>;

  const handleDeleteProductClick = async () => {
    const deleteResponse = await deleteMutation.mutateAsync(Number(id));
    if (deleteResponse.status === 200) {
      onQuerySuccess({ successMessage: t('PRODUCT.DELETE_SUCCESS') });
      navigate(FOODS_AND_PRODUCTS_ROUTE);
    } else {
      onQueryError({ errorMessage: t('PRODUCT.DELETE_FAILURE') });
    }
    setIsDeleteProductDialogOpen(false);
  };

  const handleDuplicateProduct = async () => {
    if (!product?.data) return;
    const original = product.data;
    const newProductId = await duplicateProduct(Number(id), original.name, {
      description: original.description,
      base_price: original.base_price,
      image_id: original.image_id,
      group_ids: original.groups.map((g) => g.id),
      allergen_ids: original.allergen_ids,
      tag_ids: original.tags.map((t) => t.id),
    });
    if (newProductId) {
      navigate(`${FOODS_AND_PRODUCTS_ROUTE}/${newProductId}`);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box width="100%">
        {isDeleteProductDialogOpen && (
          <DialogStyled
            title={t('PRODUCT.REALLY_WISH_TO_DELETE')}
            open={isDeleteProductDialogOpen}
            setOpen={setIsDeleteProductDialogOpen}
            onActionButtonClick={handleDeleteProductClick}
            actionButtonLabel={t('GLOBAL.DELETE')}
            onClose={() => setIsDeleteProductDialogOpen(false)}
          />
        )}
        <SubHeader
          title={t('PRODUCT.DETAILS')}
          backPath={FOODS_AND_PRODUCTS_ROUTE}
          sx={{ position: 'sticky', top: '63px', zIndex: 10, backgroundColor: 'white' }}
        >
          <Stack direction="row" gap={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setIsDeleteProductDialogOpen(true)}
            >
              {t('GLOBAL.DELETE')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={isDuplicating}
              onClick={handleDuplicateProduct}
            >
              {t('ACTIONS.DUPLICATE')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isPending}
              onClick={() => {
                methods.handleSubmit(onSubmit, (errors) => {
                  enqueueSnackbar(t('PRODUCT.VALIDATION_ERROR_ON_SAVE'), {
                    variant: 'error',
                  });
                  // eslint-disable-next-line no-console
                  console.error('Validation errors:', errors);
                })();
              }}
            >
              {t('GLOBAL.SAVE')}
            </Button>
          </Stack>
        </SubHeader>
        <Box width="100%" sx={{ backgroundColor: palette.surface.lightest }}>
          <Stack
            direction="column"
            gap={8}
            minHeight="100dvh"
            p={3}
            px={7}
            pr={{ xs: 2, xxl: 8 }}
            sx={{ maxWidth: '1700px' }}
          >
            <EditProductUtil initialProduct={product?.data} />
            <ProductPossibleModificationsUtil />
          </Stack>
        </Box>
      </Box>
    </FormProvider>
  );
};
